import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';
import {
  XMarkIcon, CheckCircleIcon, ExclamationCircleIcon,
  ChevronUpDownIcon, CheckIcon,
} from '@heroicons/react/24/outline';

interface AddMembersModalProps {
  institutionId: string;
  groupId: string;
  onClose: () => void;
  onAdded: () => void;
}

interface BulkResult {
  added: string[];
  not_found: string[];
  already_member: string[];
}

interface UserSearchResult {
  id: string;
  email: string;
  full_name: string | null;
}

const AddMembersModal: React.FC<AddMembersModalProps> = ({ institutionId, groupId, onClose, onAdded }) => {
  const { isDarkMode } = useTheme();
  const [role, setRole] = useState<'student' | 'teacher'>('student');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  // Pending picks build up as chips; submit sends the whole list to the
  // bulk endpoint. This keeps the one-shot bulk-result UI (added /
  // already_member / not_found) while replacing the typo-prone textarea
  // with type-and-select. For genuinely large batches the modal stays
  // usable — pick, pick, pick, submit.
  const [pending, setPending] = useState<UserSearchResult[]>([]);

  // Typeahead state — same shape as ManageAdminsModal / ManageSubAdminsModal.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const response = await api.get(
          `/api/institutions/${institutionId}/users/search`,
          { params: { q: trimmed, limit: 10 } }
        );
        setResults(response.data);
      } catch (err) {
        console.error('User search failed:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, institutionId]);

  const handlePick = (user: UserSearchResult | null) => {
    if (!user) return;
    // Don't double-add. The dropdown also dims existing picks below.
    if (pending.some(p => p.email.toLowerCase() === user.email.toLowerCase())) return;
    setPending(prev => [...prev, user]);
    setQuery('');
    setResults([]);
  };

  const handleRemoveChip = (email: string) => {
    setPending(prev => prev.filter(p => p.email.toLowerCase() !== email.toLowerCase()));
  };

  const handleSubmit = async () => {
    if (pending.length === 0) return;
    setSubmitting(true);
    try {
      const response = await api.post(
        `/api/institutions/${institutionId}/groups/${groupId}/members/bulk`,
        { emails: pending.map(p => p.email), role }
      );
      setResult(response.data);
      onAdded();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to add members');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingEmails = new Set(pending.map(p => p.email.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={`w-full max-w-lg mx-4 p-6 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Add Members
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!result ? (
          <>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Search and pick users
            </label>

            {/* Typeahead — single-select, picks accumulate in `pending` chips below. */}
            <Combobox value={null} onChange={handlePick}>
              <div className="relative mb-2">
                <Combobox.Input
                  className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
                    isDarkMode
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-cyan-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                  }`}
                  // displayValue intentionally always returns '' — we never
                  // want the picked email to fill the input; it goes to
                  // chips, the input clears for the next pick.
                  displayValue={() => ''}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by email or name (min 2 chars)"
                  autoComplete="off"
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </Combobox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setResults([])}
                >
                  <Combobox.Options
                    className={`absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg py-1 text-sm shadow-lg ring-1 ring-black/5 ${
                      isDarkMode ? 'bg-slate-700 text-white' : 'bg-white text-gray-900'
                    }`}
                  >
                    {searching ? (
                      <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Searching…
                      </div>
                    ) : query.trim().length < 2 ? (
                      <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Type at least 2 characters
                      </div>
                    ) : results.length === 0 ? (
                      <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        No users match &ldquo;{query}&rdquo;
                      </div>
                    ) : (
                      results.map((u) => {
                        const isPending = pendingEmails.has(u.email.toLowerCase());
                        return (
                          <Combobox.Option
                            key={u.id}
                            value={u}
                            disabled={isPending}
                            className={({ active, disabled }) =>
                              `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                disabled
                                  ? 'opacity-50 cursor-not-allowed'
                                  : active
                                    ? 'bg-cyan-500 text-white'
                                    : isDarkMode ? 'text-slate-200' : 'text-gray-900'
                              }`
                            }
                          >
                            <div className="truncate font-medium">
                              {u.full_name || u.email}
                            </div>
                            {u.full_name && (
                              <div className="truncate text-xs opacity-75">{u.email}</div>
                            )}
                            {isPending && (
                              <div className="truncate text-xs opacity-75 italic">
                                Already in list
                              </div>
                            )}
                          </Combobox.Option>
                        );
                      })
                    )}
                  </Combobox.Options>
                </Transition>
              </div>
            </Combobox>

            {/* Picked-user chips. X removes from the pending list. */}
            {pending.length > 0 ? (
              <div className={`flex flex-wrap gap-2 mb-4 p-3 rounded-lg border ${
                isDarkMode ? 'bg-slate-900/40 border-slate-700' : 'bg-gray-50 border-gray-200'
              }`}>
                {pending.map(u => (
                  <span
                    key={u.id}
                    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium ${
                      isDarkMode ? 'bg-cyan-500/15 text-cyan-200' : 'bg-cyan-50 text-cyan-700'
                    }`}
                  >
                    <span className="truncate max-w-[200px]">{u.full_name || u.email}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveChip(u.email)}
                      className={`p-0.5 rounded-full ${
                        isDarkMode ? 'hover:bg-cyan-500/30' : 'hover:bg-cyan-200'
                      }`}
                      aria-label={`Remove ${u.email}`}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Pick one or more users above. They'll appear here as chips before you submit.
              </p>
            )}

            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'student' | 'teacher')}
              className={`w-full px-4 py-2 rounded-lg border mb-4 ${
                isDarkMode
                  ? 'bg-slate-700 border-slate-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
            </select>

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg text-sm ${
                  isDarkMode ? 'text-gray-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || pending.length === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
              >
                {submitting ? 'Adding...' : `Add ${pending.length} ${pending.length === 1 ? 'Member' : 'Members'}`}
              </button>
            </div>
          </>
        ) : (
          <div>
            {result.added.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Added ({result.added.length})</span>
                </div>
                <div className={`text-sm space-y-1 pl-7 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {result.added.map(e => <div key={e}>{e}</div>)}
                </div>
              </div>
            )}

            {result.already_member.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                  <ExclamationCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Already members ({result.already_member.length})</span>
                </div>
                <div className={`text-sm space-y-1 pl-7 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {result.already_member.map(e => <div key={e}>{e}</div>)}
                </div>
              </div>
            )}

            {result.not_found.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <ExclamationCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Not signed up yet ({result.not_found.length})</span>
                </div>
                <div className={`text-sm space-y-1 pl-7 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {result.not_found.map(e => <div key={e}>{e}</div>)}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddMembersModal;
